import { terminateBrowserSession } from './api';

/**
 * Shared dashboard WebSocket connections — one socket per channel key.
 * Defers connect until the page is stable, uses exponential backoff on reconnect,
 * and keeps sockets alive briefly after the last subscriber unmounts (React remounts).
 */

const DEFAULT_CONNECT_DELAY_MS = 300;
const TEARDOWN_GRACE_MS = 600;
const BASE_RECONNECT_MS = 1000;
const MAX_RECONNECT_MS = 30000;

const channels = new Map();

function detachSocketHandlers(socket) {
  if (!socket) return;
  socket.onopen = null;
  socket.onmessage = null;
  socket.onerror = null;
  socket.onclose = null;
}

function reconnectDelayMs(attempt) {
  const exponential = Math.min(BASE_RECONNECT_MS * 2 ** attempt, MAX_RECONNECT_MS);
  const jitter = Math.random() * 0.25 * exponential;
  return Math.round(exponential + jitter);
}

class DashboardWebSocketChannel {
  constructor(channelKey, connectDelayMs) {
    this.channelKey = channelKey;
    this.connectDelayMs = connectDelayMs;
    this.subscribers = new Set();
    this.buildUrl = null;
    this.socket = null;
    this.generation = 0;
    this.reconnectAttempt = 0;
    this.reconnectTimer = null;
    this.connectTimer = null;
    this.teardownTimer = null;
    this.allowReconnect = true;
  }

  subscribe(subscriber) {
    this.subscribers.add(subscriber);
    if (this.teardownTimer) {
      clearTimeout(this.teardownTimer);
      this.teardownTimer = null;
    }
    this.scheduleConnect();
    return () => this.unsubscribe(subscriber);
  }

  unsubscribe(subscriber) {
    this.subscribers.delete(subscriber);
    if (this.subscribers.size === 0) {
      this.teardownTimer = setTimeout(() => {
        if (this.subscribers.size === 0) {
          this.destroy();
        }
      }, TEARDOWN_GRACE_MS);
    }
  }

  scheduleConnect() {
    if (this.subscribers.size === 0 || !this.buildUrl) {
      return;
    }

    const state = this.socket?.readyState;
    if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) {
      return;
    }

    if (this.connectTimer) {
      return;
    }

    this.connectTimer = setTimeout(() => {
      this.connectTimer = null;
      if (this.subscribers.size > 0) {
        this.connect();
      }
    }, this.connectDelayMs);
  }

  connect() {
    if (!this.buildUrl || this.subscribers.size === 0) {
      return;
    }

    this.generation += 1;
    const generation = this.generation;

    if (this.socket) {
      const stale = this.socket;
      this.socket = null;
      detachSocketHandlers(stale);
      try {
        stale.close();
      } catch (err) {
        /* ignore */
      }
    }

    try {
      const wsUrl = this.buildUrl();
      const socket = new WebSocket(wsUrl);
      this.socket = socket;

      socket.onopen = () => {
        if (generation !== this.generation || this.socket !== socket) {
          return;
        }
        this.reconnectAttempt = 0;
        for (const subscriber of this.subscribers) {
          subscriber.onOpen?.();
        }
      };

      socket.onmessage = (event) => {
        if (generation !== this.generation || this.socket !== socket) {
          return;
        }
        for (const subscriber of this.subscribers) {
          subscriber.onMessage?.(event);
        }
      };

      socket.onclose = (event) => {
        if (this.socket === socket) {
          this.socket = null;
        }
        detachSocketHandlers(socket);

        for (const subscriber of this.subscribers) {
          subscriber.onClose?.(event);
        }

        if (event.code === 4403) {
          terminateBrowserSession(
            'ACCOUNT_INACTIVE',
            'Your account has been deactivated. Contact your administrator.'
          );
          return;
        }
        if (event.code === 4401) {
          terminateBrowserSession(
            'SESSION_REVOKED',
            'Your session is no longer active. Please sign in again.'
          );
          return;
        }

        if (!this.allowReconnect || this.subscribers.size === 0) {
          return;
        }

        // Terminal closes: do not auto-reconnect (avoids spam).
        // Fresh subscribe/mount after access is re-enabled will connect normally.
        if (event.code === 4401 || event.code === 4400 || event.code === 4403) {
          return;
        }

        this.scheduleReconnect();
      };

      socket.onerror = () => {
        try {
          socket.close();
        } catch (err) {
          /* ignore */
        }
      };
    } catch (err) {
      for (const subscriber of this.subscribers) {
        subscriber.onError?.(err);
      }
      if (this.allowReconnect && this.subscribers.size > 0) {
        this.scheduleReconnect();
      }
    }
  }

  scheduleReconnect() {
    if (this.reconnectTimer || this.subscribers.size === 0) {
      return;
    }

    const delay = reconnectDelayMs(this.reconnectAttempt);
    this.reconnectAttempt += 1;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.subscribers.size > 0) {
        this.connect();
      }
    }, delay);
  }

  send(message) {
    const socket = this.socket;
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(message));
      return true;
    }
    return false;
  }

  getReadyState() {
    return this.socket?.readyState ?? WebSocket.CLOSED;
  }

  destroy() {
    this.allowReconnect = false;

    if (this.connectTimer) {
      clearTimeout(this.connectTimer);
      this.connectTimer = null;
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.teardownTimer) {
      clearTimeout(this.teardownTimer);
      this.teardownTimer = null;
    }

    const socket = this.socket;
    this.socket = null;
    if (socket) {
      detachSocketHandlers(socket);
      try {
        socket.close();
      } catch (err) {
        /* ignore */
      }
    }

    channels.delete(this.channelKey);
  }
}

function getOrCreateChannel(channelKey, connectDelayMs) {
  let channel = channels.get(channelKey);
  if (!channel) {
    channel = new DashboardWebSocketChannel(channelKey, connectDelayMs);
    channels.set(channelKey, channel);
  }
  return channel;
}

/**
 * Subscribe to a shared dashboard WebSocket channel.
 * Returns unsubscribe(), send(message), and getReadyState().
 */
export function subscribeDashboardWebSocket({
  channelKey,
  buildUrl,
  connectDelayMs = DEFAULT_CONNECT_DELAY_MS,
  onOpen,
  onMessage,
  onClose,
  onError,
}) {
  const channel = getOrCreateChannel(channelKey, connectDelayMs);
  channel.buildUrl = buildUrl;

  const subscriber = { onOpen, onMessage, onClose, onError };
  const unsubscribe = channel.subscribe(subscriber);

  return {
    unsubscribe,
    send: (message) => channel.send(message),
    getReadyState: () => channel.getReadyState(),
  };
}

/** Connect delays for dashboard WebSocket channels. */
export const DASHBOARD_WS_CONNECT_DELAYS = {
  unified: 300,
};
